import {
  BillingPrice,
  BillingPriceInterval,
  BillingProduct,
} from '@tribeplatform/gql-client/types'
import { useDomain } from '@tribeplatform/react-components/common/hooks'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { Link } from '@tribeplatform/react-components/Link'
import {
  useCreateSubscriptionCheckoutSession,
  useUpdateSubscriptionInterval,
  useUpdateSubscriptionPlan,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useUpgradePlanMessage } from '../../../../hooks/useUpgradePlanMessage.js'
import { useCurrentProductInformation } from '../hooks/useCurrentProductInformation.js'
import { useTrackCheckoutStarted } from '../hooks/useTrackCheckoutStarted.js'
import { CheckoutStartedQueryParams } from '../types.js'

interface Props {
  newProduct: BillingProduct
  price: BillingPrice
  className?: string
}

export const PlanCardButton = ({ newProduct, price, className }: Props) => {
  const { $t } = useI18n()
  const { trackCheckoutStarted } = useTrackCheckoutStarted()

  const { sendUpgradePlanMessage } = useUpgradePlanMessage()
  const { mutateAsync: createSession, isLoading: isCreatingSession } =
    useCreateSubscriptionCheckoutSession()
  const {
    mutateAsync: updateSubscriptionPlan,
    isLoading: isUpdatingSubscriptionPlan,
  } = useUpdateSubscriptionPlan()
  const {
    mutateAsync: updateSubscriptionInterval,
    isLoading: isUpdatingInterval,
  } = useUpdateSubscriptionInterval()
  const isLoading =
    isCreatingSession || isUpdatingSubscriptionPlan || isUpdatingInterval
  const { getResourceUrl } = useDomain()
  const { currentProduct, currentInterval, currentProductOrder, isManual } =
    useCurrentProductInformation()

  const newProductOrder = newProduct.order

  const switchBillingInterval = async (options?: { sendToast?: boolean }) => {
    const { sendToast = true } = options ?? {}
    try {
      await updateSubscriptionInterval({
        interval: price.interval,
      })
      if (sendToast) {
        toast({
          title: $t({
            defaultMessage: 'Plan updated',
            id: 'Admin.Billing.ChangePlan.Success',
          }),
          description: $t({
            defaultMessage:
              'Your plan is updated. Changes may take a few moments to appear in your account.',
            id: 'Admin.Billing.ChangePlan.SuccessDescription',
          }),
          status: 'success',
        })
      }
    } catch (e) {
      toast({
        title: $t({
          defaultMessage: 'Something went wrong',
          id: 'Admin.Billing.ChangePlan.Error',
        }),
        description: $t({
          defaultMessage: 'Unable to change plan',
          id: 'Admin.Billing.ChangePlan.ErrorDescription',
        }),
        status: 'error',
      })
    }
  }

  const openCheckout = async () => {
    const newWindow = window.open()
    const planId = newProduct.id
    const planName = newProduct.name
    const planAmount = price.amount
    const planInterval = price.interval

    const checkoutQs: CheckoutStartedQueryParams = {
      planId,
      planName,
      planAmount: `${planAmount}`,
      planInterval,
      success: 'true',
    }

    const queryParams = new URLSearchParams({ ...checkoutQs })

    /**
     * CHECKOUT_SESSION_ID is populated by stripe callback.
     * See: https://docs.stripe.com/payments/checkout/custom-success-page#modify-the-success-url
     */
    const successUrl = getResourceUrl(
      `/manage/billing?sessionId={CHECKOUT_SESSION_ID}&${queryParams.toString()}`,
    )
    const cancelUrl = getResourceUrl(
      `/manage/billing/plans?success=false&sessionId={CHECKOUT_SESSION_ID}`,
    )

    await trackCheckoutStarted({
      planId,
      planName,
      planAmount,
      planInterval,
    })

    try {
      const session = await createSession({
        priceId: price.id,
        successUrl,
        cancelUrl,
      })
      newWindow.location.href = session.url
    } catch (e) {
      newWindow.close()
      toast({
        title: $t({
          defaultMessage: 'Something went wrong',
          id: 'Admin.Billing.BillingPortal.Error',
        }),
        description: $t({
          defaultMessage: 'Unable to open billing portal',
          id: 'Admin.Billing.BillingPortal.ErrorDescription',
        }),
        status: 'error',
      })
    }
  }

  const autoUpdatePlan = async () => {
    const newWindow = window.open()
    try {
      await updateSubscriptionPlan({
        priceId: price.id,
      })
      const queryParams = new URLSearchParams()
      if (newProductOrder > currentProductOrder) {
        queryParams.append('upgrade', 'true')
      }
      newWindow.location.href = `/manage/billing?${queryParams.toString()}`
      toast({
        title: $t({
          defaultMessage: 'Plan updated',
          id: 'Admin.Billing.ChangePlan.Success',
        }),
        description: $t({
          defaultMessage:
            'Your plan is updated. Changes may take a few moments to appear in your account.',
          id: 'Admin.Billing.ChangePlan.SuccessDescription',
        }),
        status: 'success',
      })
    } catch (e) {
      newWindow.close()
      toast({
        title: $t({
          defaultMessage: 'Something went wrong',
          id: 'Admin.Billing.ChangePlan.Error',
        }),
        description: $t({
          defaultMessage: 'Unable to change plan',
          id: 'Admin.Billing.ChangePlan.ErrorDescription',
        }),
        status: 'error',
      })
    }
  }

  if (
    currentProduct?.id === newProduct?.id &&
    currentInterval === price?.interval
  ) {
    return (
      <Button fullWidth variant="secondary" disabled className={className}>
        {$t({
          defaultMessage: 'Current plan',
          id: 'Admin.Billing.Plans.CurrentPlan',
        })}
      </Button>
    )
  }

  if (currentProduct?.isEnterprise || newProduct.isEnterprise || isManual) {
    return (
      <Button
        fullWidth
        as={Link}
        href="https://bettermode.com/contact-sales"
        variant="primary"
        className={className}
      >
        {$t({
          defaultMessage: 'Contact us',
          id: 'Generics.ContactUs',
        })}
      </Button>
    )
  }

  if (
    currentProduct &&
    currentInterval &&
    price?.interval !== currentInterval
  ) {
    if (newProduct?.id === currentProduct?.id) {
      return (
        <Button
          fullWidth
          variant="primary"
          onClick={() => switchBillingInterval()}
          loading={isLoading}
          className={className}
        >
          <p className="text-sm whitespace-normal">
            {price?.interval === BillingPriceInterval.Month && (
              <T
                defaultMessage="Switch to monthly billing"
                id="Admin.Billing.Plans.SwitchToMonthlyBilling"
              />
            )}
            {price?.interval === BillingPriceInterval.Year && (
              <T
                defaultMessage="Switch to yearly billing"
                id="Admin.Billing.Plans.SwitchToYearlyBilling"
              />
            )}
          </p>
        </Button>
      )
    }
    if (newProductOrder < currentProductOrder) {
      if (!newProduct.downgradable) {
        return (
          <Button
            fullWidth
            variant="primary"
            onClick={() => sendUpgradePlanMessage(newProduct?.name)}
            loading={isLoading}
            className={className}
          >
            <T defaultMessage="Contact us" id="Generics.ContactUs" />
          </Button>
        )
      }
      return (
        <Button
          fullWidth
          variant="primary"
          onClick={async () => {
            await switchBillingInterval({ sendToast: false })
            await autoUpdatePlan()
          }}
          loading={isLoading}
          className={className}
        >
          <p className="text-sm whitespace-normal">
            {price?.interval === BillingPriceInterval.Month && (
              <T
                defaultMessage="Downgrade & switch to monthly"
                id="Admin.Billing.Plans.DowngradeAndSwitchToMonthlyBilling"
              />
            )}
            {price?.interval === BillingPriceInterval.Year && (
              <T
                defaultMessage="Downgrade & switch to yearly"
                id="Admin.Billing.Plans.DowngradeSwitchToYearlyBilling"
              />
            )}
          </p>
        </Button>
      )
    }
    return (
      <Button
        fullWidth
        variant="primary"
        onClick={async () => {
          await switchBillingInterval({ sendToast: false })
          await autoUpdatePlan()
        }}
        loading={isLoading}
        className={className}
      >
        <p className="text-sm whitespace-normal">
          {price?.interval === BillingPriceInterval.Month && (
            <T
              defaultMessage="Upgrade & switch to monthly"
              id="Admin.Billing.Plans.UpgradeAndSwitchToMonthlyBilling"
            />
          )}
          {price?.interval === BillingPriceInterval.Year && (
            <T
              defaultMessage="Upgrade & switch to yearly"
              id="Admin.Billing.Plans.UpgradeAndSwitchToYearlyBilling"
            />
          )}
        </p>
      </Button>
    )
  }

  if (newProductOrder < currentProductOrder) {
    return (
      <Button
        fullWidth
        variant="primary"
        onClick={() => {
          if (newProduct.downgradable) {
            autoUpdatePlan()
          } else {
            sendUpgradePlanMessage(newProduct?.name)
          }
        }}
        loading={isLoading}
        className={className}
      >
        {newProduct.downgradable ? (
          <T
            defaultMessage="Downgrade"
            id="Admin.Billing.Plans.DowngradeButton"
          />
        ) : (
          $t({
            defaultMessage: 'Contact us',
            id: 'Generics.ContactUs',
          })
        )}
      </Button>
    )
  }

  return (
    <Button
      fullWidth
      variant="primary"
      onClick={() => {
        if (currentProduct) {
          autoUpdatePlan()
        } else {
          openCheckout()
        }
      }}
      loading={isLoading}
      className={className}
    >
      <T defaultMessage="Upgrade" id="Admin.Billing.Plans.UpgradeButton" />
    </Button>
  )
}
